import { captureException } from '@sentry/react';
import React from 'react';
import { AiOutlineRobot } from 'react-icons/ai'; // Example AI-themed icon from react-icons

interface FancyGlobalErrorBoundaryProps {
  children: React.ReactNode;
}

interface FancyGlobalErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * A fancy error boundary for an AI-powered SaaS, styled with Tailwind CSS.
 * This component catches errors in its children, logs them, and displays a
 * user-friendly fallback UI. 
 */
export default class FancyGlobalErrorBoundary extends React.Component<
  FancyGlobalErrorBoundaryProps,
  FancyGlobalErrorBoundaryState
> {
  constructor(props: FancyGlobalErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  /**
   * Lifecycle method to update state based on the thrown error.
   */
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  /**
   * Lifecycle method to log error information or report it to an external service.
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureException({
      error,
      errorInfo
    })
  }

  /**
   * Optional event handler to handle "Reload" or any custom action.
   */
  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error } = this.state;

    if (hasError && error) {
      return (
        <div className="flex items-center justify-center w-full h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-4">
          {/* Container for the glassmorphic error card */}
          <div className="max-w-lg w-full bg-white/80 backdrop-blur-md rounded-lg shadow-lg p-6 md:p-10 text-center flex flex-col items-center animate-fadeIn">
            {/* Fancy AI icon at the top */}
            <AiOutlineRobot className="text-indigo-600 text-6xl mb-4 mx-auto" />

            <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-2">
              Oops! Something went wrong.
            </h1>

            <p className="text-sm md:text-base text-gray-600 mb-6">
              We’re sorry, but an unexpected error just occurred.
              You can try refreshing the page or contact our support team.
            </p>

            {/* Detailed error message (optional to display) */}
            {/* <div className="bg-red-50 dark:bg-red-800/40 text-red-700 dark:text-red-200 text-left rounded p-4 w-full mb-6">
              <p className="font-bold">Error Details:</p>
              <p className="text-xs md:text-sm mt-2 break-all">
                {error.message}
              </p>
            </div> */}

            {/* Buttons row */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-2">
              <button
                onClick={this.handleReload}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-5 py-2 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                Reload
              </button>
              <a
                href="/dashboard"
                className="bg-white hover:bg-gray-100 text-indigo-700 rounded-md px-5 py-2 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }

    // Normal rendering if there's no error
    return this.props.children;
  }
}
